import React, { useMemo } from 'react';

import { Stack } from '@lyearn/ui';
import { mergeProviderTagsWithTags } from '@/modules/corporate/modules/tags/components/SelectedTag/utils';
import { ActionItemFragment } from '@/modules/tasks/features/actionItems/graphql/fragments/generated/ActionItem';
import { EntityTagType, TagCategoryEnum, TagUnion, UserViewFieldSchema } from '@/types/schema';

import AttachedTagsContainer from './AttachedTagsContainer';

interface ActionItemTagsProps {
  config: UserViewFieldSchema;
  actionItem: ActionItemFragment;
  allTags: TagUnion[];
  disableInteractiveTooltip?: boolean;
}

const getTagEmptyValue = (tag?: TagUnion) => ({
  dateValue: null,
  isEditable: true,
  numberValue: null,
  optionIdValues: [],
  options: null,
  tag,
  tagId: tag?.id,
  textValue: null,
  type: tag?.type,
  users: [],
});

function ActionItemTags(props: ActionItemTagsProps) {
  const { config, actionItem, allTags, disableInteractiveTooltip = false } = props;

  const currentTag = useMemo(() => {
    const existingTag = actionItem.tags?.find((tag) => tag && tag.tagId === config.entityId);
    if (existingTag) {
      return existingTag;
    }

    // IntegrationProviderEnum.Jira tags handling via config.provider
    const tagDefinition = allTags.find((tag) => tag.id === config.entityId);
    return getTagEmptyValue(tagDefinition);
  }, [actionItem.tags, allTags, config.entityId]);

  const allProviderTagsWithEmptyValues = useMemo(() => {
    const allProviderTags = allTags.filter((tag) => tag.category === TagCategoryEnum.Field);
    return allProviderTags?.map(getTagEmptyValue);
  }, [allTags]);

  const allMergedTags = useMemo(() => {
    const tagsToMerge = (actionItem.tags || []).filter((tag): tag is EntityTagType => Boolean(tag));
    // Only add currentTag if it's not already in actionItem.tags
    const shouldAddCurrentTag =
      currentTag && !tagsToMerge.some((tag) => tag.tagId === currentTag.tagId);
    const tagsWithCurrentTag = shouldAddCurrentTag ? [...tagsToMerge, currentTag] : tagsToMerge;

    return mergeProviderTagsWithTags(
      allProviderTagsWithEmptyValues as EntityTagType[],
      tagsWithCurrentTag as EntityTagType[],
    );
  }, [actionItem.tags, allProviderTagsWithEmptyValues, currentTag]);

  return (
    <Stack className="truncate w-full h-full">
      <AttachedTagsContainer
        disableInteractiveTooltip={disableInteractiveTooltip}
        tag={currentTag as EntityTagType}
        taskId={actionItem.id}
        taskTags={allMergedTags as EntityTagType[]}
      />
    </Stack>
  );
}

export default React.memo(ActionItemTags);
